import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, Row, Col, Button } from "reactstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import Flatpickr from "react-flatpickr";

// === VALIDATION ===
const validationSchema = Yup.object({
  customerId: Yup.string().required("Customer is required"),
  vehicleId: Yup.string().required("Vehicle is required"),
  sessionId: Yup.string().required("Session is required"),
  suppliersId: Yup.string().required("Supplier is required"),

  tripType: Yup.string().required("Trip Type is required"),
  partyType: Yup.string().required("Party Type is required"),

  // Optional fields (only check if value exists)
  tollCharges: Yup.number().typeError("Must be a number").nullable(),
  otherCharges: Yup.number().typeError("Must be a number").nullable(),
  handlingCharges: Yup.number().typeError("Must be a number").nullable(),

  kmOrTrip: Yup.number().typeError("Enter a valid number").required("Required"),
  //   tripDate: Yup.string()
  //     .required("Trip Date is required")
  //     .test("not-past", "Select today or future date", (value) => {
  //       if (!value) return false;
  //       const selected = new Date(value.split("-").reverse().join("-"));
  //       const today = new Date();
  //       today.setHours(0, 0, 0, 0);
  //       return selected >= today;
  //     }),
});

const BookTripModal = ({
  isOpen,
  toggle,
  customers,
  vehicles,
  sessions,
  suppliers,
  onSubmit,
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const customerOptions =
    customers?.map((c) => ({ value: c.id, label: c.name })) || [];

  const vehicleOptions =
    vehicles?.map((v) => ({ value: v.id, label: v.vehicleNo })) || [];

  const sessionOptions =
    sessions?.map((s) => ({ value: s.id, label: s.name })) || [];

  const supplierOptions =
    suppliers?.map((se) => ({ value: se.id, label: se.name })) || [];

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Book Trip</ModalHeader>

      <ModalBody>
        <Formik
          initialValues={{
            tripInvoiceNo: "",
            customerId: "",
            vehicleId: "",
            sessionId: "",
            suppliersId: "",
            tripType: "",
            partyType: "",
            tripDate: "",
            kmOrTrip: "",
            rate: "",
            amount: "",

            tollCharges: "",
            otherCharges: "",
            handlingCharges: "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            let cleaned = { ...values };

            // Remove optional fields if empty
            ["tollCharges", "otherCharges", "handlingCharges"].forEach(
              (key) => {
                if (
                  cleaned[key] === "" ||
                  cleaned[key] === null ||
                  cleaned[key] === undefined
                ) {
                  delete cleaned[key];
                } else {
                  cleaned[key] = Number(cleaned[key]);
                }
              }
            );

            if (onSubmit) {
              onSubmit(cleaned);
            }

            toggle();
          }}
        >
          {({ values, errors, touched, setFieldValue }) => {
            const recalcTotal = (
              kmOrTripVal = values.kmOrTrip,
              rateVal = values.rate
            ) => {
              if (kmOrTripVal && rateVal) {
                setFieldValue("amount", Number(kmOrTripVal) * Number(rateVal));
              }
            };

            return (
              <Form>
                <Row className="g-3">
                  {/* LEFT COLUMN */}
                  <Col md={6}>
                    {/* CUSTOMER */}

                    <div className="">
                      <label className="form-label">Invoice No</label>
                      <Field
                        type="text"
                        name="tripInvoiceNo"
                        className="form-control"
                        value={values.tripInvoiceNo}
                        onChange={(e) =>
                          setFieldValue("tripInvoiceNo", e.target.value)
                        }
                      />
                    </div>

                    <div>
                      <label className="form-label">Customer *</label>
                      <Select
                        options={customerOptions}
                        value={customerOptions.find(
                          (opt) => opt.value === values.customerId
                        )}
                        onChange={(opt) =>
                          setFieldValue("customerId", opt?.value || "")
                        }
                        placeholder="Select Customer"
                      />
                    </div>

                    {/* VEHICLE */}
                    <div>
                      <label className="form-label mt-3">Vehicle *</label>
                      <Select
                        options={vehicleOptions}
                        value={vehicleOptions.find(
                          (opt) => opt.value === values.vehicleId
                        )}
                        onChange={(opt) => {
                          setFieldValue("vehicleId", opt?.value || "");
                          const fullObj = vehicles.find(
                            (v) => v.id === opt?.value
                          );
                          setSelectedVehicle(fullObj);

                          if (values.tripType === "TRIP") {
                            setFieldValue("rate", fullObj?.localRate || "");
                            recalcTotal(values.kmOrTrip, fullObj?.localRate);
                          } else if (values.tripType === "KM") {
                            setFieldValue("rate", fullObj?.kmRate || "");
                            recalcTotal(values.kmOrTrip, fullObj?.kmRate);
                          }
                        }}
                        placeholder="Select Vehicle"
                      />
                    </div>

                    {/* TRIP TYPE + PARTY TYPE SIDE BY SIDE */}
                    <Row className="mt-3">
                      <Col md={6}>
                        <label className="form-label">Trip Type *</label>
                        <Select
                          options={[
                            { value: "KM", label: "KM" },
                            { value: "perTrip", label: "TRIP" },
                          ]}
                          value={
                            values.tripType
                              ? {
                                  value: values.tripType,
                                  label: values.tripType,
                                }
                              : null
                          }
                          onChange={(opt) => {
                            const tripType = opt?.value || "";
                            setFieldValue("tripType", tripType);

                            if (tripType === "KM") {
                              setFieldValue("partyType", "OUTSTATION");
                              const rate = selectedVehicle?.kmRate || "";
                              setFieldValue("rate", rate);
                              recalcTotal(values.kmOrTrip, rate);
                            } else {
                              setFieldValue("partyType", "LOCAL");
                              const rate = selectedVehicle?.localRate || "";
                              setFieldValue("rate", rate);
                              recalcTotal(values.kmOrTrip, rate);
                            }
                          }}
                          placeholder="Select Trip Type"
                        />
                      </Col>

                      <Col md={6}>
                        <label className="form-label">Party Type *</label>
                        <Field
                          name="partyType"
                          className="form-control"
                          disabled
                        />
                        {touched.partyType && errors.partyType && (
                          <div className="text-danger small">
                            {errors.partyType}
                          </div>
                        )}
                      </Col>
                    </Row>

                    {/* KM / TRIP */}
                    <div className="mt-3">
                      <label className="form-label">
                        {values.tripType === "KM"
                          ? "KM Count"
                          : "Number of Trips"}{" "}
                        *
                      </label>
                      <Field
                        type="number"
                        name="kmOrTrip"
                        className={`form-control ${
                          touched.kmOrTrip && errors.kmOrTrip
                            ? "is-invalid"
                            : ""
                        }`}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFieldValue("kmOrTrip", val);
                          recalcTotal(val, values.rate);
                        }}
                      />
                    </div>

                    {/* RATE */}
                    <div className="mt-3">
                      <label className="form-label">Rate </label>
                      <Field
                        type="number"
                        name="rate"
                        className="form-control"
                        disabled
                      />
                    </div>
                  </Col>

                  {/* RIGHT COLUMN */}
                  <Col md={6}>
                    {/* SESSION */}
                    <div>
                      <label className="form-label">Session *</label>
                      <Select
                        options={sessionOptions}
                        value={sessionOptions.find(
                          (opt) => opt.value === values.sessionId
                        )}
                        onChange={(opt) =>
                          setFieldValue("sessionId", opt?.value || "")
                        }
                        placeholder="Select Session"
                      />
                    </div>

                    {/* SUPPLIER */}
                    <div className="mt-3">
                      <label className="form-label">Supplier *</label>
                      <Select
                        options={supplierOptions}
                        value={supplierOptions.find(
                          (opt) => opt.value === values.suppliersId
                        )}
                        onChange={(opt) =>
                          setFieldValue("suppliersId", opt?.value || "")
                        }
                        placeholder="Select Supplier"
                      />
                    </div>

                    {/* TRIP DATE */}
                    <div className="mt-3">
                      <label className="form-label">Trip Date *</label>
                      <Flatpickr
                        options={{
                          dateFormat: "d-m-Y",
                          maxDate: "today",
                        }}
                        value={values.tripDate}
                        onChange={(dateArr) => {
                          if (dateArr.length > 0) {
                            const d = dateArr[0];
                            const formatted =
                              ("0" + d.getDate()).slice(0 - 2) +
                              "-" +
                              ("0" + (d.getMonth() + 1)).slice(0 - 2) +
                              "-" +
                              d.getFullYear();
                            setFieldValue("tripDate", formatted);
                          }
                        }}
                        className={`form-control ${
                          touched.tripDate && errors.tripDate
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder="Select Trip Date"
                      />
                      {touched.tripDate && errors.tripDate && (
                        <div className="text-danger small">
                          {errors.tripDate}
                        </div>
                      )}
                    </div>

                    {/* OPTIONAL CHARGES */}
                    <div className="mt-3">
                      <label className="form-label">Toll Charges</label>
                      <Field
                        type="number"
                        name="tollCharges"
                        className="form-control"
                      />
                    </div>

                    <div className="mt-3">
                      <label className="form-label">Other Charges</label>
                      <Field
                        type="number"
                        name="otherCharges"
                        className="form-control"
                      />
                    </div>

                    <div className="mt-3">
                      <label className="form-label">Handling Charges</label>
                      <Field
                        type="number"
                        name="handlingCharges"
                        className="form-control"
                      />
                    </div>
                  </Col>
                </Row>

                <Col md={6}>
                  <div className="mt-4">
                    <label className="form-label fw-bold">Total Amount</label>
                    <Field
                      type="number"
                      name="amount"
                      className="form-control fw-bold"
                      disabled
                    />
                  </div>
                </Col>
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    color="secondary"
                    onClick={toggle}
                    className=" btn-sm me-2"
                  >
                    Cancel
                  </Button>
                  <Button className="btn-sm" color="success" type="submit">
                    Save Trip
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default BookTripModal;
